/**
 * Realtor Verification Page
 *
 * Single-screen submission of RERA registration + photo ID for admin
 * review. The same surface renders the four post-submit states so the
 * realtor lands here from the dashboard regardless of where they are in
 * the verification lifecycle:
 *   - approved: confirmation card; this page becomes a status read-out.
 *   - pending+uploaded: read-only "in review" card.
 *   - rejected: re-submission form pre-filled with the prior RERA number,
 *     plus the admin's rejection notes inline.
 *   - default (no submission yet, or pending without upload): the form.
 *
 * The backend POST /api/v1/realtors/me/verification flips
 * `idProofUploaded=true` and resets `reviewStatus=pending` — the
 * `rera_verified` / `id_verified` flags only flip via admin approval.
 * That state machine is reflected here and on the dashboard banner.
 */

import { ArrowLeft, CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SingleImageUpload } from "@/components/ui/image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
	getMyRealtorProfile,
	type RealtorProfile,
	submitRealtorVerification,
} from "@/features/realtor/services/realtor-api.service";
import { StudioButton } from "@/features/studio/theme";

// Four orthogonal load states, mirrored from the dashboard. Critically,
// `needs-onboarding` is distinct from `error`: the API returns `{ success:
// true, profile: null }` when a realtor has a userType but never landed on
// the onboarding form. That's a "complete your profile" empty state, not a
// fetch failure — sending those users to a generic error card would loop
// them back here via the dashboard.
type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "needs-onboarding" }
	| { status: "loaded"; profile: RealtorProfile };

export function RealtorVerificationPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });

	const loadProfile = useCallback(async () => {
		setLoad({ status: "loading" });
		const result = await getMyRealtorProfile();
		if (!result.success) {
			setLoad({
				status: "error",
				message: result.error ?? "Couldn't load your profile.",
			});
			return;
		}
		if (!result.profile) {
			setLoad({ status: "needs-onboarding" });
			return;
		}
		setLoad({ status: "loaded", profile: result.profile });
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const result = await getMyRealtorProfile();
			if (cancelled) return;
			if (!result.success) {
				setLoad({
					status: "error",
					message: result.error ?? "Couldn't load your profile.",
				});
				return;
			}
			if (!result.profile) {
				setLoad({ status: "needs-onboarding" });
				return;
			}
			setLoad({ status: "loaded", profile: result.profile });
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-6"
				data-testid="verification-loading"
			>
				<Skeleton className="h-24 w-full" />
				<Skeleton className="h-48 w-full" />
			</div>
		);
	}

	// Fetch genuinely failed (network, 5xx). Offer a retry rather than
	// a one-way "back to dashboard" so the realtor isn't forced to round-
	// trip just to retry the same call.
	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{load.message}
						</p>
						<div className="flex justify-center gap-2">
							<Button
								variant="outline"
								className="gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg"
								onClick={loadProfile}
							>
								<RefreshCw className="h-4 w-4" />
								{t("realtor.dashboard.retry")}
							</Button>
							<StudioButton
								variant="primary"
								onClick={() => navigate("/dashboard")}
							>
								{t("realtor.verification.backToDashboard")}
							</StudioButton>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// 200 OK with profile=null — realtor has a userType but never finished
	// onboarding. The dashboard renders the same empty state, so we mirror
	// its destination instead of bouncing through /dashboard.
	if (load.status === "needs-onboarding") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<h2
							className="text-lg font-semibold"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.dashboard.completeProfileTitle")}
						</h2>
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("realtor.dashboard.completeProfileSubtitle")}
						</p>
						<StudioButton
							variant="primary"
							onClick={() => navigate("/onboarding/realtor")}
						>
							{t("realtor.dashboard.completeProfileCta")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<RealtorVerificationContent
			profile={load.profile}
			onProfileUpdate={(profile) => setLoad({ status: "loaded", profile })}
		/>
	);
}

interface ContentProps {
	profile: RealtorProfile;
	onProfileUpdate: (profile: RealtorProfile) => void;
}

function RealtorVerificationContent({
	profile,
	onProfileUpdate,
}: ContentProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const isApproved = profile.reraVerified && profile.idVerified;
	const isPending =
		profile.reviewStatus === "pending" && profile.idProofUploaded;
	const isRejected = profile.reviewStatus === "rejected";

	const [reraNumber, setReraNumber] = useState(profile.reraNumber ?? "");
	const [idProofUrl, setIdProofUrl] = useState<string | null>(null);
	const [reraError, setReraError] = useState<string | null>(null);
	const [idProofError, setIdProofError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [justSubmitted, setJustSubmitted] = useState(false);

	if (isApproved) {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-8 text-center space-y-4">
						<div
							className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full"
							style={{ backgroundColor: "var(--color-dashboard-pill-done-bg)" }}
						>
							<CheckCircle2
								className="h-6 w-6"
								style={{ color: "var(--color-dashboard-pill-done)" }}
							/>
						</div>
						<div className="space-y-1">
							<h1
								className="text-xl font-semibold"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.verification.approvedTitle")}
							</h1>
							<p
								className="text-sm max-w-md mx-auto"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.verification.approvedSubtitle")}
							</p>
						</div>
						<StudioButton
							variant="secondary"
							onClick={() => navigate("/dashboard")}
						>
							{t("realtor.verification.backToDashboard")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (isPending && !justSubmitted) {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-8 text-center space-y-4">
						<div
							className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full"
							style={{
								backgroundColor: "var(--color-dashboard-pill-awaiting-bg)",
							}}
						>
							<ShieldCheck
								className="h-6 w-6"
								style={{ color: "var(--color-dashboard-pill-awaiting)" }}
							/>
						</div>
						<div className="space-y-1">
							<h1
								className="text-xl font-semibold"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.verification.pendingTitle")}
							</h1>
							<p
								className="text-sm max-w-md mx-auto"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.verification.pendingSubtitle")}
							</p>
						</div>
						<StudioButton
							variant="secondary"
							onClick={() => navigate("/dashboard")}
						>
							{t("realtor.verification.backToDashboard")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (justSubmitted) {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-8 text-center space-y-4">
						<div
							className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full"
							style={{ backgroundColor: "var(--color-dashboard-pill-info-bg)" }}
						>
							<CheckCircle2
								className="h-6 w-6"
								style={{ color: "var(--color-dashboard-pill-info)" }}
							/>
						</div>
						<div className="space-y-1">
							<h1
								className="text-xl font-semibold"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.verification.successTitle")}
							</h1>
							<p
								className="text-sm max-w-md mx-auto"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.verification.successSubtitle")}
							</p>
						</div>
						<StudioButton
							variant="primary"
							onClick={() => navigate("/dashboard")}
						>
							{t("realtor.verification.backToDashboard")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const trimmedRera = reraNumber.trim();
		const nextReraError = trimmedRera
			? null
			: t("realtor.verification.reraRequired");
		const nextIdError = idProofUrl
			? null
			: t("realtor.verification.idProofRequired");
		setReraError(nextReraError);
		setIdProofError(nextIdError);
		// Narrow defensively: even with the early return below, the closure
		// over `idProofUrl` doesn't propagate through the conditional, so we
		// pin a local that TS can prove is non-null at the call site.
		if (nextReraError || nextIdError || !idProofUrl) return;
		const idProofUrlAtSubmit = idProofUrl;

		setIsSubmitting(true);
		const result = await submitRealtorVerification({
			reraNumber: trimmedRera,
			idProofUrl: idProofUrlAtSubmit,
		});
		setIsSubmitting(false);

		if (!result.success || !result.profile) {
			toast.error(
				result.error ?? t("realtor.verification.submitErrorFallback"),
			);
			return;
		}

		onProfileUpdate(result.profile);
		setJustSubmitted(true);
	};

	const reraErrorId = "rera-error";
	const idProofErrorId = "id-proof-error";

	return (
		<form
			onSubmit={handleSubmit}
			className="bg-dashboard-surface min-h-screen p-6 space-y-6"
		>
			<button
				type="button"
				onClick={() => navigate("/dashboard")}
				className="flex items-center gap-2 text-sm transition-colors"
				style={{ color: "var(--color-dashboard-muted)" }}
			>
				<ArrowLeft className="h-4 w-4" />
				Back to dashboard
			</button>

			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-2">
					<div className="flex items-center gap-2">
						<ShieldCheck
							className="h-5 w-5"
							style={{ color: "var(--color-dashboard-accent-dark)" }}
						/>
						<h1
							className="text-xl font-semibold"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.verification.pageTitle")}
						</h1>
					</div>
					<p
						className="text-sm leading-relaxed"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.verification.pageSubtitle")}
					</p>
				</CardContent>
			</Card>

			{isRejected && (
				<Card
					style={{
						borderColor: "var(--color-dashboard-pill-risk)",
						backgroundColor: "var(--color-dashboard-pill-risk-bg)",
					}}
				>
					<CardContent className="p-5 space-y-1">
						<p
							className="text-sm font-semibold"
							style={{ color: "var(--color-dashboard-pill-risk)" }}
						>
							{t("realtor.verification.rejectedTitle")}
						</p>
						<p
							className="text-xs"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{profile.reviewNotes ??
								t("realtor.verification.rejectedSubtitle")}
						</p>
					</CardContent>
				</Card>
			)}

			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-5">
					<div className="space-y-2">
						<Label htmlFor="rera-number">
							{t("realtor.verification.reraLabel")}
						</Label>
						<Input
							id="rera-number"
							value={reraNumber}
							onChange={(e) => {
								setReraNumber(e.target.value);
								if (reraError) setReraError(null);
							}}
							placeholder={t("realtor.verification.reraPlaceholder")}
							autoComplete="off"
							aria-invalid={reraError ? true : undefined}
							aria-describedby={reraError ? reraErrorId : undefined}
							className="border-dashboard-border bg-dashboard-bg text-dashboard-text"
						/>
						<p
							className="text-xs"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("realtor.verification.reraHelp")}
						</p>
						{reraError && (
							<p
								id={reraErrorId}
								className="text-xs"
								style={{ color: "var(--color-dashboard-pill-risk)" }}
							>
								{reraError}
							</p>
						)}
					</div>

					<fieldset
						aria-describedby={idProofError ? idProofErrorId : undefined}
						className="space-y-2 border-0 p-0"
					>
						<legend
							className="text-sm font-medium leading-none"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.verification.idProofLabel")}
						</legend>
						<p
							className="text-xs"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("realtor.verification.idProofHelp")}
						</p>
						<SingleImageUpload
							label={t("realtor.verification.idProofLabel")}
							currentUrl={idProofUrl ?? undefined}
							onUpload={(url) => {
								setIdProofUrl(url);
								setIdProofError(null);
							}}
							onRemove={() => setIdProofUrl(null)}
						/>
						{idProofError && (
							<p
								id={idProofErrorId}
								className="text-xs"
								style={{ color: "var(--color-dashboard-pill-risk)" }}
							>
								{idProofError}
							</p>
						)}
					</fieldset>
				</CardContent>
			</Card>

			<div className="flex justify-end">
				<StudioButton type="submit" variant="primary" disabled={isSubmitting}>
					{isSubmitting
						? t("realtor.verification.submitting")
						: t("realtor.verification.submit")}
				</StudioButton>
			</div>
		</form>
	);
}
